import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";

const ACCENT = "#fcd657";

/* ─── Star field background ──────────────────────────────────────────────── */
function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.2, a: Math.random(),
      speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed; if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.15;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

/* ─── Image Lightbox ─────────────────────────────────────────────────────── */
function ImageLightbox({ media, onClose, canEdit, onUpdateCaption }) {
  const [caption, setCaption] = useState(media.caption || "");
  const [saving, setSaving] = useState(false);

  async function saveCaption() {
    setSaving(true);
    await onUpdateCaption(media.id, caption);
    setSaving(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(12px, 3vw, 24px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ maxWidth: 800, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6" }}>{media.file_name}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 28, cursor: "pointer", padding: 8 }}>&times;</button>
        </div>
        {media.file_type?.startsWith("image/") ? (
          <img src={media.file_url} alt={media.file_name} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 2, marginBottom: 16 }} />
        ) : (
          <div style={{ padding: 40, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", textAlign: "center", marginBottom: 16 }}>
            <a href={media.file_url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, color: ACCENT, letterSpacing: 2 }}>DOWNLOAD FILE</a>
          </div>
        )}
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 6 }}>Context / Notes</div>
        {canEdit ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={3}
              style={{ flex: 1, minWidth: 200, padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", resize: "vertical" }}
              placeholder="Add context about this file..." />
            <button onClick={saveCaption} disabled={saving}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 18px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", alignSelf: "flex-start", opacity: saving ? 0.4 : 1 }}>
              {saving ? "..." : "Save"}
            </button>
          </div>
        ) : (
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", lineHeight: 1.6 }}>{caption || "No context added."}</p>
        )}
      </div>
    </div>
  );
}

/* ─── Media Gallery ──────────────────────────────────────────────────────── */
function MediaGallery({ items, canEdit, resourceId, profileId, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [newName, setNewName] = useState("");
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setNewName(file.name.replace(/\.[^.]+$/, ""));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setNewName(file.name.replace(/\.[^.]+$/, ""));
  }

  function handleDragOver(e) { e.preventDefault(); if (canEdit) setDragOver(true); }
  function handleDragLeave(e) { e.preventDefault(); setDragOver(false); }

  async function confirmUpload() {
    if (!pendingFile || !newName.trim()) return;
    setUploading(true);
    const ext = pendingFile.name.split(".").pop();
    const path = `resources/${resourceId}/${crypto.randomUUID()}.${ext}`;
    const { error: upErr } = await supabase.storage.from("resource-media").upload(path, pendingFile);
    if (upErr) { alert("Upload failed: " + upErr.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from("resource-media").getPublicUrl(path);
    await supabase.from("resource_media").insert([{
      resource_id: resourceId, uploaded_by: profileId,
      file_url: urlData?.publicUrl || "", file_name: newName.trim(),
      file_type: pendingFile.type, caption: "",
    }]);
    setPendingFile(null);
    setNewName("");
    setUploading(false);
    onRefresh();
  }

  async function deleteItem(id) {
    if (!window.confirm("Remove this file?")) return;
    await supabase.from("resource_media").delete().eq("id", id);
    onRefresh();
  }

  async function updateCaption(id, caption) {
    await supabase.from("resource_media").update({ caption }).eq("id", id);
    onRefresh();
  }

  return (
    <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
      style={{ position: "relative", minHeight: canEdit ? 60 : undefined }}>

      {/* Drag overlay */}
      {dragOver && canEdit && (
        <div style={{ position: "absolute", inset: 0, zIndex: 10, background: `${ACCENT}15`, border: `2px dashed ${ACCENT}88`, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, color: ACCENT, textTransform: "uppercase" }}>Drop file here</span>
        </div>
      )}

      {canEdit && (
        <div style={{ marginBottom: 12 }}>
          <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.zip,.xlsx,.csv" onChange={handleFileSelect} style={{ display: "none" }} />
          {pendingFile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name this file..."
                style={{ flex: 1, minWidth: 150, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none" }} />
              <button onClick={confirmUpload} disabled={uploading || !newName.trim()}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, padding: "8px 16px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (uploading || !newName.trim()) ? 0.4 : 1 }}>
                {uploading ? "Uploading..." : "Upload"}
              </button>
              <button onClick={() => { setPendingFile(null); setNewName(""); }}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "8px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={() => fileInputRef.current?.click()}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
                + Add File
              </button>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", letterSpacing: 1 }}>or drag &amp; drop</span>
            </div>
          )}
        </div>
      )}

      {items.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(clamp(110px, 20vw, 160px), 1fr))", gap: 10 }}>
          {items.map(m => (
            <div key={m.id} style={{ position: "relative", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", transition: "border-color 0.2s" }}
              onClick={() => setLightboxItem(m)}>
              {m.file_type?.startsWith("image/") ? (
                <img src={m.file_url} alt={m.file_name} style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "100%", height: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.03)" }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 18, color: "rgba(255,255,255,0.2)" }}>◇</span>
                </div>
              )}
              <div style={{ padding: "6px 8px", background: "rgba(0,0,0,0.4)" }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.file_name}</div>
                {m.caption && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.caption}</div>}
              </div>
              {canEdit && (
                <button onClick={e => { e.stopPropagation(); deleteItem(m.id); }}
                  style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,0.7)", border: "1px solid rgba(239,83,80,0.5)", color: "#ef5350", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {items.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 20 }}>NO FILES</div>
      )}

      {lightboxItem && (
        <ImageLightbox media={lightboxItem} onClose={() => setLightboxItem(null)} canEdit={canEdit} onUpdateCaption={updateCaption} />
      )}
    </div>
  );
}

/* ─── Links Manager ──────────────────────────────────────────────────────── */
function LinksManager({ links, canEdit, resourceId, profileId, onRefresh, profiles }) {
  const [adding, setAdding] = useState(false);
  const [linkName, setLinkName] = useState("");
  const [linkUrl, setLinkUrl] = useState("");

  async function addLink() {
    if (!linkName.trim() || !linkUrl.trim()) return;
    await supabase.from("resource_links").insert([{
      resource_id: resourceId, link_name: linkName.trim(), link_url: linkUrl.trim(), created_by: profileId,
    }]);
    setLinkName(""); setLinkUrl(""); setAdding(false);
    onRefresh();
  }

  async function removeLink(id) {
    if (!window.confirm("Remove this link?")) return;
    await supabase.from("resource_links").delete().eq("id", id);
    onRefresh();
  }

  function getCreatorName(userId) {
    const p = (profiles || []).find(pr => pr.id === userId);
    return p?.discord_username || p?.discord_display_name || "Unknown";
  }

  return (
    <div>
      {links.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          {links.map(l => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)" }}>
              <a href={l.link_url} target="_blank" rel="noopener noreferrer"
                style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#4fc3f7", textDecoration: "none", flex: 1 }}>
                {l.link_name}
              </a>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", whiteSpace: "nowrap" }}>
                by {getCreatorName(l.created_by)}
              </span>
              {canEdit && (
                <button onClick={() => removeLink(l.id)}
                  style={{ background: "none", border: "none", color: "#ef5350", fontSize: 14, cursor: "pointer", padding: "0 4px", opacity: 0.6 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {links.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 20 }}>NO LINKS</div>
      )}
      {canEdit && (
        adding ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input value={linkName} onChange={e => setLinkName(e.target.value)} placeholder="Link name..."
              style={{ flex: "1 1 120px", minWidth: 120, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none" }} />
            <input value={linkUrl} onChange={e => setLinkUrl(e.target.value)} placeholder="https://..."
              style={{ flex: "2 1 200px", minWidth: 200, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none" }} />
            <button onClick={addLink} disabled={!linkName.trim() || !linkUrl.trim()}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, padding: "8px 14px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (!linkName.trim() || !linkUrl.trim()) ? 0.4 : 1 }}>Add</button>
            <button onClick={() => { setAdding(false); setLinkName(""); setLinkUrl(""); }}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "8px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)}
            style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
            + Add Link
          </button>
        )
      )}
    </div>
  );
}

/* ─── Section wrapper ────────────────────────────────────────────────────── */
function Section({ title, color = ACCENT, children, collapsible = false }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 24, border: `1px solid ${color}22`, background: `${color}04` }}>
      <div onClick={collapsible ? () => setOpen(!open) : undefined}
        style={{ padding: "14px clamp(14px, 3vw, 20px)", borderBottom: open ? `1px solid ${color}15` : "none", display: "flex", alignItems: "center", gap: 10, cursor: collapsible ? "pointer" : "default" }}>
        {collapsible && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: `${color}88`, transition: "transform 0.2s", transform: open ? "rotate(90deg)" : "rotate(0)" }}>▶</span>}
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color, textTransform: "uppercase" }}>{title}</span>
      </div>
      {open && <div style={{ padding: "clamp(14px, 3vw, 20px)" }}>{children}</div>}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   RESOURCE DETAIL PAGE
   Shows all resources in a category as cards. Click a card to expand its
   full detail (notes, links, files/images). Super admins can edit/add.
   ═══════════════════════════════════════════════════════════════════════════ */
export default function ResourceDetailPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { profiles: preloadedProfiles } = useFleetData();

  const [category, setCategory] = useState(null);
  const [resources, setResources] = useState([]);
  const [selectedResource, setSelectedResource] = useState(null);
  const [links, setLinks] = useState([]);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editNotes, setEditNotes] = useState("");
  const [addingNew, setAddingNew] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const isSuperAdmin = profile?.perm_super_admin === true || profile?.admin_tier === "super_admin";

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const { data: cat } = await supabase.from("resource_categories").select("*").eq("id", categoryId).single();
      setCategory(cat);
      const { data: res } = await supabase.from("resources").select("*").eq("category_id", categoryId).order("sort_order", { ascending: true });
      setResources(res || []);
    } catch (e) { /* */ }
    setLoading(false);
  }, [categoryId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // When a resource is selected, load its links and media
  useEffect(() => {
    if (!selectedResource) { setLinks([]); setMedia([]); return; }
    loadResourceData(selectedResource.id);
  }, [selectedResource?.id]);

  async function loadResourceData(resId) {
    const [{ data: lnks }, { data: med }] = await Promise.all([
      supabase.from("resource_links").select("*").eq("resource_id", resId).order("created_at", { ascending: true }),
      supabase.from("resource_media").select("*").eq("resource_id", resId).order("uploaded_at", { ascending: true }),
    ]);
    setLinks(lnks || []);
    setMedia(med || []);
  }

  async function addNewResource() {
    if (!newTitle.trim()) return;
    setSaving(true);
    const maxSort = resources.reduce((max, r) => Math.max(max, r.sort_order || 0), 0);
    const { data } = await supabase.from("resources").insert([{
      category_id: categoryId, title: newTitle.trim(), notes: "", sort_order: maxSort + 1, created_by: profile.id,
    }]).select().single();
    setNewTitle("");
    setAddingNew(false);
    setSaving(false);
    await loadAll();
    if (data) {
      setSelectedResource(data);
      setEditNotes("");
    }
  }

  async function saveNotes() {
    if (!selectedResource) return;
    setSaving(true);
    await supabase.from("resources").update({ notes: editNotes, updated_at: new Date().toISOString() }).eq("id", selectedResource.id);
    setSelectedResource(prev => ({ ...prev, notes: editNotes }));
    // Also update in the list
    setResources(prev => prev.map(r => r.id === selectedResource.id ? { ...r, notes: editNotes } : r));
    setSaving(false);
  }

  async function deleteResource(id) {
    if (!window.confirm("Delete this resource and all its links/files?")) return;
    await supabase.from("resources").delete().eq("id", id);
    if (selectedResource?.id === id) setSelectedResource(null);
    loadAll();
  }

  function getCreatorName(userId) {
    const p = (preloadedProfiles || []).find(pr => pr.id === userId);
    return p?.discord_username || p?.discord_display_name || "Unknown";
  }

  function refreshResourceData() {
    if (selectedResource) loadResourceData(selectedResource.id);
  }

  if (loading) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, color: `${ACCENT}66` }}>
        LOADING...
      </div>
    );
  }

  if (!category) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16 }}>
        <Nav accentColor={ACCENT} />
        <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(239,83,80,0.7)", marginTop: 80 }}>CATEGORY NOT FOUND</div>
        <Link to="/resources" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 2, color: ACCENT, textDecoration: "none" }}>&larr; Back to Resources</Link>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::selection { background: #fcd657; color: #05070f; } a { text-decoration: none; color: inherit; } .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; } .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #fcd657; transition: width 0.3s ease; } .nav-link:hover { color: #fcd657; } .nav-link:hover::after { width: 100%; } .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); } ::-webkit-scrollbar-thumb { background: rgba(252,214,87,0.3); }`}</style>
      <div className="scanline" />
      <StarField />
      <Nav accentColor={ACCENT} />

      <div style={{ position: "relative", zIndex: 1, paddingTop: "calc(64px + env(safe-area-inset-top, 0px) + 40px)", paddingBottom: 80, paddingLeft: "clamp(16px, 4vw, 60px)", paddingRight: "clamp(16px, 4vw, 60px)", maxWidth: 1200, margin: "0 auto" }}>

        {/* Breadcrumb + Header */}
        <div style={{ marginBottom: 32 }}>
          <Link to="/resources" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, color: `${ACCENT}88`, textTransform: "uppercase", textDecoration: "none", transition: "color 0.2s" }}>
            &larr; All Resources
          </Link>
          <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(20px, 3.5vw, 30px)", letterSpacing: 3, color: "#e8eaf6", marginTop: 12, marginBottom: 6 }}>
            {category.name}
          </h1>
          {category.description && (
            <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.4)", lineHeight: 1.6, maxWidth: 600 }}>{category.description}</p>
          )}
          <div style={{ width: 60, height: 2, background: ACCENT, marginTop: 12 }} />
        </div>

        {/* Two-panel layout: resource list + detail */}
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>

          {/* Left: Resource cards */}
          <div style={{ flex: "1 1 300px", minWidth: 260 }}>
            {/* Add new button (super admin) */}
            {isSuperAdmin && (
              <div style={{ marginBottom: 16 }}>
                {addingNew ? (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input value={newTitle} onChange={e => setNewTitle(e.target.value)} placeholder="Resource title..."
                      onKeyDown={e => { if (e.key === "Enter") addNewResource(); }}
                      style={{ flex: 1, minWidth: 180, padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}44`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 15, borderRadius: 2, outline: "none" }} />
                    <button onClick={addNewResource} disabled={saving || !newTitle.trim()}
                      style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, padding: "10px 18px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (saving || !newTitle.trim()) ? 0.4 : 1 }}>
                      {saving ? "..." : "Create"}
                    </button>
                    <button onClick={() => { setAddingNew(false); setNewTitle(""); }}
                      style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "10px 14px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
                  </div>
                ) : (
                  <button onClick={() => setAddingNew(true)}
                    style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "10px 20px", background: `${ACCENT}12`, border: `1px solid ${ACCENT}44`, color: ACCENT, cursor: "pointer", transition: "all 0.2s", width: "100%" }}>
                    + Add New Resource
                  </button>
                )}
              </div>
            )}

            {/* Resource list */}
            {resources.length === 0 && (
              <div style={{ textAlign: "center", padding: 48, border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 24, color: "rgba(255,255,255,0.08)", marginBottom: 10 }}>◇</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.25)" }}>No resources in this category yet.</div>
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {resources.map(res => {
                const isSelected = selectedResource?.id === res.id;
                return (
                  <div key={res.id}
                    onClick={() => { setSelectedResource(res); setEditNotes(res.notes || ""); }}
                    style={{
                      padding: "clamp(14px, 2vw, 20px)", cursor: "pointer", transition: "all 0.2s",
                      border: isSelected ? `1px solid ${ACCENT}66` : "1px solid rgba(255,255,255,0.08)",
                      background: isSelected ? `${ACCENT}0a` : "rgba(255,255,255,0.02)",
                    }}
                    onMouseEnter={e => { if (!isSelected) { e.currentTarget.style.borderColor = "rgba(252,214,87,0.3)"; e.currentTarget.style.background = "rgba(252,214,87,0.04)"; }}}
                    onMouseLeave={e => { if (!isSelected) { e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)"; e.currentTarget.style.background = "rgba(255,255,255,0.02)"; }}}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(12px, 2vw, 15px)", color: "#e8eaf6", letterSpacing: 1, marginBottom: 4 }}>
                          {res.title}
                        </div>
                        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.25)", letterSpacing: 1 }}>
                          Added by {getCreatorName(res.created_by)}
                        </div>
                      </div>
                      {isSelected && (
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: ACCENT }}>▶</span>
                      )}
                    </div>
                    {res.notes && (
                      <div style={{ marginTop: 8, fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.35)", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                        {res.notes}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Selected resource detail */}
          <div style={{ flex: "2 1 400px", minWidth: 300 }}>
            {!selectedResource ? (
              <div style={{ textAlign: "center", padding: 80, border: "1px solid rgba(255,255,255,0.04)", background: "rgba(255,255,255,0.01)" }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, color: "rgba(255,255,255,0.06)", marginBottom: 12 }}>←</div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.2)" }}>Select a resource to view details</div>
              </div>
            ) : (
              <div>
                {/* Resource header */}
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
                  <div>
                    <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(18px, 3vw, 24px)", color: "#e8eaf6", letterSpacing: 2, marginBottom: 4 }}>
                      {selectedResource.title}
                    </h2>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)", letterSpacing: 1 }}>
                      Added by <span style={{ color: ACCENT }}>{getCreatorName(selectedResource.created_by)}</span>
                      {" · "}
                      {new Date(selectedResource.created_at).toLocaleDateString()}
                    </div>
                  </div>
                  {isSuperAdmin && (
                    <button onClick={() => deleteResource(selectedResource.id)}
                      style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, textTransform: "uppercase", padding: "6px 14px", background: "none", border: "1px solid rgba(239,83,80,0.3)", color: "#ef5350", cursor: "pointer", transition: "all 0.2s" }}>
                      Delete
                    </button>
                  )}
                </div>

                {/* Notes */}
                <Section title="Notes" collapsible>
                  {isSuperAdmin ? (
                    <div>
                      <textarea value={editNotes} onChange={e => setEditNotes(e.target.value)} rows={6}
                        style={{ width: "100%", padding: "12px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}22`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, lineHeight: 1.7, borderRadius: 2, outline: "none", resize: "vertical" }}
                        placeholder="Add notes for this resource..." />
                      {editNotes !== (selectedResource.notes || "") && (
                        <button onClick={saveNotes} disabled={saving}
                          style={{ marginTop: 8, fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 18px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", opacity: saving ? 0.4 : 1 }}>
                          {saving ? "Saving..." : "Save Notes"}
                        </button>
                      )}
                    </div>
                  ) : (
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                      {selectedResource.notes || "No notes yet."}
                    </div>
                  )}
                </Section>

                {/* Links */}
                <Section title="Links & References" color="#4fc3f7" collapsible>
                  <LinksManager
                    links={links}
                    canEdit={isSuperAdmin}
                    resourceId={selectedResource.id}
                    profileId={profile?.id}
                    onRefresh={refreshResourceData}
                    profiles={preloadedProfiles}
                  />
                </Section>

                {/* Files & Images */}
                <Section title="Files & Images" color="#ce93d8" collapsible>
                  <MediaGallery
                    items={media}
                    canEdit={isSuperAdmin}
                    resourceId={selectedResource.id}
                    profileId={profile?.id}
                    onRefresh={refreshResourceData}
                  />
                </Section>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
